import { createFileRoute, Link } from '@tanstack/react-router'
import { Box, Typography } from '@mui/material'
import { DocPage } from '../components/docs/DocPage'
import { Section, P, DefList, C, Automate, H3, MEASURE } from '../components/docs/prose'

export const Route = createFileRoute('/docs/payment-grid')({ component: PaymentGrid })

const SECTIONS = [
  { id: 'question', title: 'The question it answers' },
  { id: 'reading', title: 'Reading a cell' },
  { id: 'legend', title: 'Colours and marks' },
  { id: 'totals', title: 'Totals and the month check' },
  { id: 'outflows', title: 'Money out by category' },
]

/** The colour key, built from the same tokens the grid itself renders with. */
function Legend() {
  const rows: Array<[string, string, string, string]> = [
    ['var(--inflow-text)', '1,204', 'Paid, nothing left over', 'The statement was cleared, or there was no balance to clear.'],
    ['var(--danger-text)', '50 ▲', 'Paid, but short', 'A revolving payee where the statement was not cleared. The triangle means money is still on the card, accruing interest.'],
    ['var(--warn-text)', '318 ?', 'Paid, never posted', 'Someone marked it paid, the grace window passed, and no matching transaction ever appeared.'],
    ['var(--danger-text)', '1,204 !', 'Overdue', 'Still owed, and the due date has passed.'],
    ['var(--warn-text)', '1,204 !', 'Outstanding', 'Still owed, not yet late.'],
    ['var(--text-faint)', '—', 'Nothing due', 'A bill exists for that month with no balance.'],
    ['var(--text-faint)', '·', 'No bill', 'That payee had no bill that month at all.'],
  ]

  return (
    <Box sx={{ my: 3, maxWidth: `${MEASURE + 12}ch` }}>
      {rows.map(([colour, sample, title, body]) => (
        <Box key={title} sx={{
          display: 'flex', gap: 2.5, py: 1.5, alignItems: 'baseline',
          borderTop: '1px solid var(--border-soft)',
        }}>
          <Typography className="fw-num" sx={{
            color: colour, minWidth: 76, textAlign: 'right', flexShrink: 0, fontSize: '0.9rem',
          }}>
            {sample}
          </Typography>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '0.92rem' }}>{title}</Typography>
            <Typography sx={{ color: 'var(--text-secondary)', fontSize: '0.92rem', lineHeight: 1.65 }}>
              {body}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  )
}

function PaymentGrid() {
  return (
    <DocPage
      path="/docs/payment-grid"
      title="Payment grid"
      lede={
        <>
          Every payee down the side, every month across the top, one year at a
          time. It exists to answer a single question quickly: did everything
          with a balance actually get cleared?
        </>
      }
      sections={SECTIONS}
    >
      <Section id="question" title="The question it answers">
        <P>
          A list of bills tells you about now. The grid tells you about
          pattern. Scanning across a row shows a payee that crept up in March
          and never came back down; scanning down a column shows the month
          everything landed at once.
        </P>
        <P>
          Arrow buttons either side of the year move a year at a time, and you
          cannot page into the future. The toggle above the table narrows to
          credit cards only, which is usually the version you want, because
          cards are where a partial payment costs you money.
        </P>
        <P>
          Payees with no activity at all in the year are dropped from the
          table. An empty row is noise.
        </P>
      </Section>

      <Section id="reading" title="Reading a cell">
        <P>
          A cell is one bill: one payee, one month. What it prints is the
          amount that matters — what was paid if something was paid, what is
          still owed if not — plus a mark when something needs attention.
        </P>
        <P>
          Hovering gives the full record: the amount, the date, the funding
          account, the due date, the statement balance, the posted date, how
          completely it was covered, and its{' '}
          <Link to="/docs/reconciliation" style={{ color: 'var(--accent-text)' }}>
            reconciliation state
          </Link>. Everything the{' '}
          <Link to="/docs/bills" style={{ color: 'var(--accent-text)' }}>bills model</Link>{' '}
          holds is in that tooltip.
        </P>
      </Section>

      <Section id="legend" title="Colours and marks">
        <P>
          Three colours and three marks, meaning the same things everywhere in
          the app.
        </P>
        <Legend />
        <H3>Why partial payments are red</H3>
        <P>
          A card paid to the minimum is not a smaller version of paying it off.
          It is the state where interest starts, and it looks identical to a
          clean payment in any tracker holding one amount per bill. Red with a
          triangle is deliberate: this is the cell that costs money.
        </P>
      </Section>

      <Section id="totals" title="Totals and the month check">
        <P>
          The last column totals what each payee was paid over the year. The
          footer row totals each month, and next to each month total is the
          check that gives the grid its point:
        </P>
        <DefList
          items={[
            ['A tick', 'Every bill with a balance that month was cleared.'],
            ['An amber circle', 'At least one was not. Hovering says how many.'],
            ['Nothing', 'Nothing was due that month.'],
          ]}
        />
        <P>
          Above the table, three banners summarise the same thing in words: the
          months that were not fully cleared, how many statements were not paid
          in full and how much is still revolving, and how many bills are
          marked paid but never posted.
        </P>
      </Section>

      <Section id="outflows" title="Money out by category">
        <P>
          Underneath the grid is a year of spending by category — a monthly
          trend line, a share-of-year breakdown, and a stacked bar per month.
        </P>
        <P>
          These come from the ledger rather than from the bills table, and only
          from Expense accounts. That distinction matters: paying your Visa is
          not spending, it is settling a debt whose purchases were already
          counted when they happened. Counting both would double every dollar
          you put on a card.
        </P>
      </Section>

      <Automate>
        The grid is only as complete as your payee list, and you should not be
        building that by hand. FolioWorth proposes payees from your own
        transaction history — see{' '}
        <Link to="/docs/automating" style={{ color: 'var(--accent-text)' }}>
          detected bills
        </Link>. Rows fill themselves in once bills reconcile against imported
        transactions.
      </Automate>
    </DocPage>
  )
}
